import React from 'react'
import { BeamKonvaVisualization, CrossSectionKonva } from '../../components/BeamVisualizationKit'
import CrossSectionDetailsTable from '../../components/CrossSectionDetailsTable'
import { summarizeShearReinforcement } from '../../shearReinforcement'
import BeamDiagramKonva from '../../components/BeamDiagramKonva'
import { LimitStateSelection, SectionMaterialStrength, SMBeamModel } from '../../types'
import { ResultsDiagramType } from '../../hooks/useResultsViewState'
import { useTranslation, TranslationKey } from '@structura/i18n'

interface DiagramDataMap
{
  momentDiagram?: unknown
  shearDiagram?: unknown
  displacementDiagram?: unknown
  axialDiagram?: unknown
  axialDisplacementDiagram?: unknown
  [key: string]: unknown
}

interface ResultsVisualizationPanelProps
{
  model: SMBeamModel
  currentMaterial: string
  timberKind: 'glulam' | 'solid'
  sectionMaterialStrength: SectionMaterialStrength | null | undefined
  analysisSucceeded: boolean
  selectedDiagram: ResultsDiagramType
  diagramScale: number
  showValues: boolean
  showMaxMin: boolean
  valueDecimals: 2 | 4
  valueDensity: number
  valueFontSize?: number
  activeDiagramSet: DiagramDataMap | undefined
  activeSupportReactions: any
  displayedLimitState: LimitStateSelection
  displayedLeadingCategory: string
  leadingBadgeText: string
}

function resolveDiagramKey(diagram: ResultsDiagramType): keyof DiagramDataMap
{
  switch (diagram)
  {
    case 'moment':
      return 'momentDiagram'
    case 'shear':
      return 'shearDiagram'
    case 'displacement':
      return 'displacementDiagram'
    case 'axial':
      return 'axialDiagram'
    case 'axialDisp':
      return 'axialDisplacementDiagram'
    default:
      return 'momentDiagram'
  }
}

function resolveDiagramLabelKey(diagram: ResultsDiagramType): TranslationKey
{
  switch (diagram)
  {
    case 'moment':
      return 'beam.results.labelMoment'
    case 'shear':
      return 'beam.results.labelShear'
    case 'displacement':
      return 'beam.results.labelDeflection'
    case 'axial':
      return 'beam.results.labelAxial'
    case 'axialDisp':
      return 'beam.results.labelAxialDisp'
    default:
      return 'beam.results.labelReactions'
  }
}

function resolveDiagramIcon(diagram: ResultsDiagramType): string
{
  switch (diagram)
  {
    case 'moment':
      return 'bi-bezier2'
    case 'shear':
      return 'bi-scissors'
    case 'displacement':
      return 'bi-arrow-down-up'
    case 'axial':
      return 'bi-arrow-left-right'
    case 'axialDisp':
      return 'bi-arrows-move'
    default:
      return 'bi-arrow-up'
  }
}

// Note under the elastic deflection diagrams: the creep/cracking story differs per material,
// and steel has no creep — nothing to point the reader at, so no note.
function resolveDeflectionNoteKey(material: string): TranslationKey | null
{
  switch (material.toLowerCase())
  {
    case 'timber':
      return 'beam.results.deflectionNoCreepNoteTimber'
    case 'concrete':
      return 'beam.results.deflectionNoCreepNoteConcrete'
    default:
      return null
  }
}

const ResultsVisualizationPanel: React.FC<ResultsVisualizationPanelProps> = ({
  model,
  currentMaterial,
  timberKind,
  sectionMaterialStrength,
  analysisSucceeded,
  selectedDiagram,
  diagramScale,
  showValues,
  showMaxMin,
  valueDecimals,
  valueDensity,
  valueFontSize,
  activeDiagramSet,
  activeSupportReactions,
  displayedLimitState,
  displayedLeadingCategory,
  leadingBadgeText
}) => {
  const { t } = useTranslation()
  const deflectionNoteKey: TranslationKey | null = resolveDeflectionNoteKey(currentMaterial)
  return (
  <div>
    <div className="card mb-3">
      <div className="card-header">
        <h5 className="card-title mb-0">{t('beam.header.results')}</h5>
        <div className="mt-2">
          <span className="badge bg-primary me-2">
            <i className={`${resolveDiagramIcon(selectedDiagram)} me-1`}></i>
            {t(resolveDiagramLabelKey(selectedDiagram))}
          </span>
          <span className="badge bg-dark me-2">
            {displayedLimitState === 'ULS' && t('beam.results.badgeUls')}
            {displayedLimitState === 'SLS_CHARACTERISTIC' && t('beam.results.badgeSlsChar')}
            {displayedLimitState === 'SLS_FREQUENT' && t('beam.results.badgeSlsFreq')}
            {displayedLimitState === 'SLS_QUASIPERMANENT' && t('beam.results.badgeSlsQuasi')}
          </span>
          <span className="badge bg-secondary me-2">{leadingBadgeText}</span>
          {analysisSucceeded && (
            <span className="badge bg-success">
              <i className="bi bi-check-circle me-1"></i>
              {t('beam.results.dataAvailable')}
            </span>
          )}
        </div>
      </div>
      <div className="card-body">
        {(selectedDiagram === 'displacement' || selectedDiagram === 'axialDisp') && deflectionNoteKey !== null && (
          <div className="alert alert-info py-2 small mb-2">
            <i className="bi bi-info-circle me-1"></i>
            {t(deflectionNoteKey)}
          </div>
        )}
        {analysisSucceeded ? (
          <BeamDiagramKonva
            key="beam-diagram-main"
            model={model}
            diagramType={selectedDiagram as any}
            diagramData={selectedDiagram === 'reactions' ? undefined : (activeDiagramSet?.[resolveDiagramKey(selectedDiagram)] as any)}
            reactionData={selectedDiagram === 'reactions' ? activeSupportReactions : undefined}
            scale={diagramScale}
            showValues={showValues}
            showMaxMin={showMaxMin}
            valueDecimals={valueDecimals}
            valueDensity={valueDensity}
            valueFontSize={valueFontSize}
            limitState={displayedLimitState}
            leadingCategory={displayedLeadingCategory}
          />
        ) : (
          <BeamKonvaVisualization
            model={model}
            material={currentMaterial}
            storageKey="beamVisualization_results"
          />
        )}
      </div>
    </div>

    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">{t('beam.geometry.crossSection')}</h5>
      </div>
      <div className="card-body">
        <div className="row">
          <div className="col-md-6">
            <CrossSectionKonva
              crossSection={model.crossSection}
              material={currentMaterial}
              timberKind={currentMaterial === 'timber' ? timberKind : undefined}
              {...(currentMaterial === 'concrete'
                ? {
                    rebar: {
                      cover: ((model as any).rebarZones?.[0]?.cover) ?? 30,
                      layers: (((model as any).rebarZones?.[0]?.layers) ?? []).map((layer: any) => ({
                        z: layer.z,
                        diameter: layer.diameter,
                        count: layer.count
                      })),
                      stirrups: (model as any).rebarZones?.[0]?.stirrups
                        ? {
                            diameter: (model as any).rebarZones[0].stirrups.diameter,
                            spacing: (model as any).rebarZones[0].stirrups.spacing,
                            legs: (model as any).rebarZones[0].stirrups.legs
                          }
                        : undefined
                    }
                  }
                : {})}
            />
          </div>
          <div className="col-md-6">
            <CrossSectionDetailsTable
              crossSection={model.crossSection}
              material={model.crossSection.material || currentMaterial}
              timberKind={(model.crossSection.material || currentMaterial) === 'timber' ? timberKind : undefined}
              materialStrength={sectionMaterialStrength ?? undefined}
              shearReinforcement={summarizeShearReinforcement(model)}
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  )
}

export default ResultsVisualizationPanel


